import React from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import { MediaQuery } from 'foundation-sites/js/foundation.util.mediaQuery';
import { Translate } from 'core/translate';
import { combineClassNames } from 'core/css';
import { SvgIcon } from 'core/icons';
import { ButtonText, ThemedButton } from 'core/controls';

const getRefHeight = ref => {
  if (!ref || !ref.current) {
    return 0;
  }
  return ref.current.offsetHeight;
};

export class ModalContent extends React.Component {
  constructor(props) {
    super(props);
    this.headerRef = React.createRef();
    this.bodyRef = React.createRef();
    this.footerRef = React.createRef();
  }

  componentDidMount() {
    let isOverflow = false;
    const reveal = document.querySelector('.reveal');

    this._intervalId = setInterval(() => {
      if (!MediaQuery.atLeast('large')) {
        return;
      }

      const modalHeight =
        getRefHeight(this.headerRef) +
        getRefHeight(this.bodyRef) +
        getRefHeight(this.footerRef);
      const maxHeight = window.innerHeight * 0.85;
      const isModalLargerThanMax = modalHeight > maxHeight;

      if (isModalLargerThanMax && !isOverflow) {
        isOverflow = true;
        reveal.classList.add('reveal--overflow');
        return;
      }

      if (!isModalLargerThanMax && isOverflow) {
        isOverflow = false;
        reveal.classList.remove('reveal--overflow');
      }
    }, 100);
  }

  componentWillUnmount() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
    }
  }

  render() {
    const { children } = this.props;
    return (
      <div className="modal">
        {React.Children.map(children, child => {
          return React.cloneElement(child, {
            headerRef: this.headerRef,
            bodyRef: this.bodyRef,
            footerRef: this.footerRef
          });
        })}
      </div>
    );
  }
}

export const ModalHeader = ({
  icon,
  text,
  analyticsKey,
  hideClose,
  headerRef,
  className,
  onClose
}) => {
  const closeButton = hideClose ? null : (
    <button
      onClick={onClose}
      className="modal-header__close stat-button-close"
      data-dtm={analyticsKey || text}>
      <SvgIcon name="close" />
    </button>
  );
  return (
    <div
      className={combineClassNames(['modal-header', className])}
      ref={headerRef}>
      {icon ? (
        <SvgIcon name={icon} className="modal-header__title-icon" />
      ) : null}
      <div
        className="modal-header__title"
        dangerouslySetInnerHTML={{ __html: text }}
      />
      {closeButton}
    </div>
  );
};

export const ModalBody = ({ children, bodyRef, className }) => {
  return (
    <div className={`modal-body ${className || ''}`}>
      <div className="modal-body__inner" ref={bodyRef}>
        {children}
      </div>
    </div>
  );
};

export const ModalFooter = ({ children, footerRef, className }) => (
  <div
    className={combineClassNames(['modal-footer', className])}
    ref={footerRef}>
    <div className="modal-footer__inner">{children}</div>
  </div>
);

export const ModalFooterMessage = ({ children }) => (
  <div className="modal-footer__message">{children}</div>
);

export const ModalFooterButtons = ({ children }) => (
  <div className="modal-footer__buttons modal-footer__buttons--inline">
    {children}
  </div>
);

export const ModalFooterCancelButton = props => {
  return (
    <ThemedButton
      {...props}
      theme={{
        fallback: 'button--secondary',
        buick: 'button--link',
        gmc: 'button--link button--invert'
      }}
      analytics="text">
      <ButtonText>
        <Translate value="byo.page.general.cancel" />
      </ButtonText>
    </ThemedButton>
  );
};

export const ModalFooterPrimaryButton = props => {
  const { children } = props;
  return (
    <ThemedButton
      {...props}
      primary
      theme={{ gmc: 'button--invert' }}
      analytics="text">
      <ButtonText>
        {children ? children : <Translate value="byo.page.general.ok" />}
      </ButtonText>
    </ThemedButton>
  );
};

export const Modal = ({
  children,
  isOpen,
  contentLabel,
  className,
  onRequestClose
}) => {
  return (
    <ReactModal
      isOpen={isOpen}
      className={`reveal ${className}`}
      overlayClassName="reveal-overlay"
      contentLabel={contentLabel}
      onRequestClose={onRequestClose}
      appElement={document.getElementById('byo-web-ui')}>
      <ModalContent>{children}</ModalContent>
    </ReactModal>
  );
};

Modal.defaultProps = {
  isOpen: false,
  className: ''
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  contentLabel: PropTypes.string,
  className: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired
};

export default Modal;
